/**
 * Theme corporativo de Farmaconsulting Transacciones (FCT)
 *
 * Combina:
 * - Manual de Identidad Corporativa (marzo 2020): paleta de marca
 *   (verde, gris, negro corporativos; paleta de marketing/gráficos).
 * - Convenciones de UI consolidadas en la aplicación GCvsc:
 *   colores semánticos (success/warning/error/info) con la paleta
 *   estándar de Ant Design, que ofrece mejor semáforo visual.
 *
 * Uso:
 *   import { ConfigProvider } from 'antd';
 *   import { fctTheme } from './theme';
 *
 *   <ConfigProvider theme={fctTheme}>
 *     <App />
 *   </ConfigProvider>
 */

import type { ThemeConfig } from 'antd';

// ---------------------------------------------------------------------------
// Paleta corporativa
// ---------------------------------------------------------------------------

export const fctColors = {
  // -------------------------------------------------------------------------
  // Marca (Manual de Identidad Corporativa, §3.1) — INMUTABLES
  // -------------------------------------------------------------------------
  verdeCorporativo: '#5a7c30',   // Pantone 364 C — primario de UI, "FARMA" del logo
  grisCorporativo: '#848483',    // Pantone 423 C — texto secundario, "CONSULTING"
  negroCorporativo: '#131510',   // Pantone Black 6 C — texto principal

  // -------------------------------------------------------------------------
  // Marca extendida (Manual §3.2 y §3.3) — para fondos, presentaciones, gráficos
  // -------------------------------------------------------------------------
  azulMarketing: '#44576e',      // Pantone 7545 U — fondos formales / hero sections
  verdeAgua: '#55917A',          // presentaciones
  azulVerdoso: '#4F8A86',        // presentaciones
  oliva: '#989328',              // acento de presentaciones (NO usar como warning)
  azulComplementario: '#55869E', // series secundarias en gráficos
  morado: '#9C748C',             // series terciarias
  marronOscuro: '#977B5F',       // acentos discretos
  marronClaro: '#E9E5DC',        // fondos suaves cálidos

  // -------------------------------------------------------------------------
  // Semánticos (paleta Ant Design — convención GCvsc)
  // Se usan en `colorSuccess`/`colorWarning`/`colorError`/`colorInfo` y en
  // todos los Tag/Badge/Statistic que comuniquen estado.
  // -------------------------------------------------------------------------
  success: '#52c41a',     // AntD green-6 — Realizada / OK
  warning: '#fa8c16',     // AntD orange-6 — Pendiente
  error: '#f5222d',       // AntD red-6 — bloqueo / error
  info: '#1890ff',        // AntD blue-6 — enlaces / processing

  // -------------------------------------------------------------------------
  // Estados de operación (convención GCvsc, sección 1 del manual UI)
  // Alias semánticos sobre la paleta de arriba para que el código sea legible.
  // -------------------------------------------------------------------------
  estadoPendiente: '#fa8c16',    // = warning (orange-6)
  estadoRealizado: '#52c41a',    // = success (green-6)
  estadoDescartado: '#8c8c8c',   // gris claro neutro
  estadoTodos: '#595959',        // gris oscuro para "Todos" en filtros

  // Estados específicos de traslados (GCvsc §1)
  estadoFormato: '#1890ff',          // blue-6
  estadoCambioNombre: '#722ed1',     // purple-6

  // -------------------------------------------------------------------------
  // Tipos de incidencia (convención GCvsc §2)
  // -------------------------------------------------------------------------
  incidenciaBloqueo: '#f5222d',      // E1, E2 — red
  incidenciaInvariante: '#722ed1',   // I1, I2 — purple
  incidenciaAnomalia: '#fa541c',     // F7, E3+ — volcano (AntD)

  // -------------------------------------------------------------------------
  // Fondos y utilidades de UI
  // -------------------------------------------------------------------------
  blanco: '#FFFFFF',
  grisFondo: '#F5F5F5',          // fondo de Content
  grisBorde: '#D9D9D9',          // borde estándar
  bordeSeparador: '#F0F0F0',     // borde discreto entre filas/secciones
  fondoCard: '#FAFAFA',          // fondo de Cards de estadísticas (GCvsc §5)
  fondoError: '#FFF2F0',         // fondo de detalles de error expandidos (GCvsc §6)

  // -------------------------------------------------------------------------
  // Indicadores de progreso (convención GCvsc §7)
  // Pares (fondo, borde) para los contenedores de batch progress, y strokes
  // específicos para barras de fase.
  // -------------------------------------------------------------------------
  progresoActivoFondo: '#E6F7FF',     // azul-1 — fondo en curso
  progresoActivoBorde: '#91D5FF',     // azul-3 — borde en curso
  progresoOkFondo: '#F6FFED',         // verde-1 — fondo completado ok
  progresoOkBorde: '#B7EB8F',         // verde-3 — borde completado ok
  progresoErrorFondo: '#FFF7E6',      // gold-1 — fondo con errores
  progresoErrorBorde: '#FFD591',      // gold-3 — borde con errores

  // Strokes para Progress y iconos de fase
  faseActivaStroke: '#1677FF',        // azul para fase en curso (LoadingOutlined)
  faseErrorStroke: '#FAAD14',         // gold-6 — Progress strokeColor si hay errores
  fasePendienteIcono: '#D9D9D9',      // gris pendiente (MinusCircleOutlined)
  errorCriticoIcono: '#FF4D4F',       // red-5 — CloseCircleFilled (algo más claro que `error`)
} as const;

// ---------------------------------------------------------------------------
// Theme config para Ant Design 5.x
// ---------------------------------------------------------------------------

export const fctTheme: ThemeConfig = {
  token: {
    // Primario: verde corporativo (identidad de marca)
    colorPrimary: fctColors.verdeCorporativo,

    // Semánticos: paleta Ant Design estándar (mejor semáforo visual que la
    // paleta del manual, que no fue concebida para estados de UI).
    colorSuccess: fctColors.success,
    colorWarning: fctColors.warning,
    colorError: fctColors.error,
    colorInfo: fctColors.info,

    // Color de enlaces: azul AntD (convención web universal)
    colorLink: fctColors.info,
    colorLinkHover: '#40a9ff',   // AntD blue-5
    colorLinkActive: '#096dd9',  // AntD blue-7

    // Tipografía: Roboto (manual §4.2)
    fontFamily:
      '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", ' +
      '"Helvetica Neue", Arial, sans-serif',

    // Texto: negro corporativo (no negro puro)
    colorTextBase: fctColors.negroCorporativo,

    borderRadius: 4,
    wireframe: false,
  },

  components: {
    Layout: {
      headerBg: fctColors.blanco,
      headerColor: fctColors.negroCorporativo,
      headerHeight: 72,
      headerPadding: '0 24px',
      siderBg: fctColors.blanco,
      bodyBg: fctColors.grisFondo,
    },

    Menu: {
      itemSelectedBg: fctColors.marronClaro,
      itemSelectedColor: fctColors.verdeCorporativo,
      itemHoverColor: fctColors.verdeCorporativo,
      horizontalItemSelectedColor: fctColors.verdeCorporativo,
    },

    Button: {
      primaryShadow: 'none',
      defaultShadow: 'none',
      fontWeight: 500,
    },

    Card: {
      headerBg: fctColors.blanco,
      colorBorderSecondary: fctColors.grisBorde,
    },

    Table: {
      headerBg: fctColors.marronClaro,
      headerColor: fctColors.negroCorporativo,
      headerSortActiveBg: fctColors.marronClaro,
      rowHoverBg: '#FAFAFA',
    },

    Statistic: {
      titleFontSize: 14,
      contentFontSize: 28,
      colorTextHeading: fctColors.verdeCorporativo,
    },

    Tag: {
      defaultBg: fctColors.marronClaro,
      defaultColor: fctColors.negroCorporativo,
    },

    Typography: {
      titleMarginTop: 0,
      titleMarginBottom: '0.5em',
    },
  },
};

// ---------------------------------------------------------------------------
// URLs de logos en la web corporativa (referencia primaria)
// ---------------------------------------------------------------------------

export const fctLogoUrls = {
  // Logo "35 aniversario" vertical — uso institucional general
  logoVertical35:
    'https://www.farmaconsulting.es/wp-content/uploads/2026/01/LOGO_FCT_35_A_VERTICAL.png',
  // Logo "Nº 1 en transmisiones"
  logoNumeroUno:
    'https://www.farmaconsulting.es/wp-content/uploads/2025/05/logo_farmaconsulting.png',
  // Logo Asesoramiento Patrimonial 3.0
  logoAP30:
    'https://www.farmaconsulting.es/wp-content/uploads/2026/03/logo_asesoramiento_patrimonial-3.png',
  // Logo AP 3.0 + "el mundo ha cambiado"
  logoAP30MundoCambiado:
    'https://www.farmaconsulting.es/wp-content/uploads/2025/09/LOGO-AP-FCT.png',
  // Logo AP 3.0 + "bienvenidos a la innovación"
  logoAP30Innovacion:
    'https://www.farmaconsulting.es/wp-content/uploads/2025/07/logo_asesoramiento_patrimonial-1.jpg',
  // Logo "35 años dando vida a tus proyectos"
  logoDandoVida:
    'https://www.farmaconsulting.es/wp-content/uploads/2026/01/Logo-35-anos-dando-vida.png',
} as const;

export type FctLogoKey = keyof typeof fctLogoUrls;
